import { FixedMarker, FixedMarkerOverrides } from 'baseui/map-marker';
import { memo } from 'react';
import { Marker } from 'react-map-gl/mapbox';
import { useParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { useAnimatedLocation } from '../../../hooks/useAnimatedLocation';
import { useCamera } from '../../../hooks/useCamera';
import useUpdateEffect from '../../../hooks/useUpdateEffect';
import { mapViewState } from '../../../storage/map';
import { assetLngLatsState, assetStates } from '../../../storage/tracking';
import VehicleIcon from './VehicleIcon';

const AnimatedVehicleMarker = memo(({ state }: { state: any }) => {
  const { lat, lng } = useAnimatedLocation(state.position_latitude, state.position_longitude);

  return (
    <Marker
      latitude={lat}
      longitude={lng}
      rotation={state.position_direction}
    >
      <VehicleIcon
        vehicleType={state.asset.vehicle?.vehicleType}
        assetType={state.asset.assetType}
        color={state.asset.vehicle?.color}
      />
    </Marker>
  );
});

function TrackingMarkers() {
  const { assetId } = useParams();
  const states = useRecoilValue(assetStates(assetId || ''));
  const lngLats = useRecoilValue(assetLngLatsState(assetId || ''));
  const { fitToLngLats } = useCamera();
  const { zoom } = useRecoilValue(mapViewState);

  const fixedMarkerOverrides: FixedMarkerOverrides = {
    Root: {
      style: () => ({
        transform: `translateY(-50%)`,
      }),
    },
  };

  useUpdateEffect(() => {
    if (!lngLats.length) return;

    fitToLngLats(lngLats);
  }, [lngLats, fitToLngLats]);

  if (!states?.length) return null;

  const hidePositions = zoom < 14;

  return (
    <>
      {states.map((state, index) => {
        const isFirst = index === 0;
        const isLast = index === states.length - 1;
        const isBetween = !isFirst && !isLast;

        if (isBetween && hidePositions) return null;

        if (isLast) {
          return (
            <AnimatedVehicleMarker
              key={`anim-${index}`}
              state={state}
            />
          );
        }

        return (
          <Marker
            latitude={state.position_latitude}
            longitude={state.position_longitude}
            key={index}
          >
            {isFirst && (
              <FixedMarker
                label="Inicio"
                size="small"
                overrides={fixedMarkerOverrides}
              />
            )}
            {isBetween && (
              <FixedMarker
                size={'xx-small-circle'}
                needle="none"
              />
            )}
          </Marker>
        );
      })}
    </>
  );
}

export default memo(TrackingMarkers);
